import { DayPrayerTimes, PrayerDisplay } from '../types/prayer';

export async function loadLocalPrayerTimes(date: Date): Promise<DayPrayerTimes | null> {
  const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  const monthsLong = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  try {
    const response = await fetch(`/data/${month}_prayer_times.json`);
    if (!response.ok) return null;

    const data: DayPrayerTimes[] = await response.json();
    const dateStr = `${monthsLong[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

    return data.find(d => d.date === dateStr) || null;
  } catch {
    return null;
  }
}

export async function fetchPrayerTimesFromAPI(lat: number, lng: number, date: Date): Promise<DayPrayerTimes | null> {
  try {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    const response = await fetch(
      `https://api.aladhan.com/v1/timings/${day}-${month}-${year}?latitude=${lat}&longitude=${lng}&method=2`
    );

    if (!response.ok) return null;

    const data = await response.json();
    const timings = data.data.timings;

    const formatTime = (time24: string) => {
      const [hours, minutes] = time24.split(':');
      const h = parseInt(hours);
      const h12 = h % 12 || 12;
      return `${h12}:${minutes}`;
    };

    const monthsLong = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const daysLong = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    return {
      date: `${monthsLong[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`,
      day: daysLong[date.getDay()],
      fajr: { begins: formatTime(timings.Fajr), iqamah: formatTime(timings.Fajr) },
      sunrise: formatTime(timings.Sunrise),
      zuhr: { begins: formatTime(timings.Dhuhr), iqamah: formatTime(timings.Dhuhr) },
      asr: {
        standard: formatTime(timings.Asr),
        hanafi: formatTime(timings.Asr),
        begins: formatTime(timings.Asr),
        iqamah: formatTime(timings.Asr)
      },
      maghrib: { begins: formatTime(timings.Maghrib), iqamah: formatTime(timings.Maghrib) },
      isha: { begins: formatTime(timings.Isha), iqamah: formatTime(timings.Isha) }
    };
  } catch {
    return null;
  }
}

export function convertToPrayerDisplays(dayTimes: DayPrayerTimes): PrayerDisplay[] {
  return [
    { name: 'Fajr', nameArabic: 'الفجر', time: dayTimes.fajr.begins, key: 'fajr' },
    { name: 'Sunrise', nameArabic: 'الشروق', time: dayTimes.sunrise, key: 'sunrise' },
    { name: 'Zuhr', nameArabic: 'الظهر', time: dayTimes.zuhr.begins, key: 'zuhr' },
    { name: 'Asr', nameArabic: 'العصر', time: dayTimes.asr?.begins || dayTimes.asr?.standard || '', key: 'asr' },
    { name: 'Maghrib', nameArabic: 'المغرب', time: dayTimes.maghrib.begins, key: 'maghrib' },
    { name: 'Isha', nameArabic: 'العشاء', time: dayTimes.isha.begins, key: 'isha' }
  ];
}

export function parseTimeToMinutes(timeStr: string, prayerKey?: string): number {
  if (!timeStr) return 0;

  const matchWithMeridiem = timeStr.match(/(\d+):(\d+)\s*(am|pm)/i);
  if (matchWithMeridiem) {
    let hours = parseInt(matchWithMeridiem[1]);
    const minutes = parseInt(matchWithMeridiem[2]);
    const meridiem = matchWithMeridiem[3].toLowerCase();

    if (meridiem === 'pm' && hours !== 12) hours += 12;
    if (meridiem === 'am' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  const matchSimple = timeStr.match(/(\d+):(\d+)/);
  if (!matchSimple) return 0;

  let hours = parseInt(matchSimple[1]);
  const minutes = parseInt(matchSimple[2]);

  if (prayerKey) {
    if (prayerKey === 'fajr' || prayerKey === 'sunrise') {
      if (hours === 12) hours = 0;
    } else if (prayerKey === 'zuhr') {
      if (hours !== 12) hours += 12;
    } else if (prayerKey === 'asr' || prayerKey === 'maghrib' || prayerKey === 'isha') {
      if (hours !== 12) hours += 12;
    }
  }

  return hours * 60 + minutes;
}

export function getNextPrayer(prayers: PrayerDisplay[], currentTime: Date): string {
  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();

  for (const prayer of prayers) {
    if (prayer.key === 'sunrise') continue;
    const prayerMinutes = parseTimeToMinutes(prayer.time, prayer.key);
    if (prayerMinutes > currentMinutes) {
      return prayer.key;
    }
  }

  return 'fajr';
}
